import React, { useEffect, useState } from 'react';
import { injectIntl, FormattedMessage, FormattedPlural } from 'react-intl';
import { i18nIntl, getRemotedbUrl, isNotEmpty } from '../../lib/ep.js';
import { useRankingWithSpan } from '../../lib/hooks.js';
import { useParams } from 'react-router-dom';

import ActionLink from '../builder/ActionLink';
import Spinner from '../builder/Spinner';
import SelectMultiple from '../builder/SelectMultiple.js';

import SingleGallery, { fakeShortDescription, GalleryTags, getVisualName } from '../front/SingleGallery.js';
import LeveledHTMLText from '../front/helpers/LeveledHTMLText.js';
import { localizeFields } from '../builder/DynamicFields.js';

const Wrapper = ({ promotion, stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const in_layer = (props.in_layer != undefined) ? props.in_layer : false;
	if(in_layer) {
		return (
			<div>{props.children}</div>
		)
	}

	const default_description_text = '<h1>' + i18n("Contest Gallery") + '</h1><p>' + i18n("These are the multimedia albums of the participants.") + '</p>';
	const page_introduction = stage?.voting_ranking_description != undefined ? stage.voting_ranking_description : (stage.main_description != undefined && stage.main_description != "") ? stage.main_description : default_description_text;

	let main_image = false;
	let main_video = false;
	if(stage.main_image != "") {
		main_image = stage.full_main_image;
	} else if(stage.main_video != "") {
		main_video = stage.main_video;
	}

	if (stage?.voting_ranking_image != undefined) {
		main_image = stage.full_voting_ranking_image;
		main_video = false;
	}

	return (
		<div class="flex_cols">
			{(main_image || main_video) && (
				<div class="flex_col col_1">
					<div class="main_media_flex">
						{(main_image) && (
							<img src={main_image} alt="" style={{maxWidth:'100%'}} />
						)}

						{(main_video) && (
							<VideoEmbed
								video_url={main_video.full_url}
								video_thumb={main_video.image}
							/>
						)}
					</div>
				</div>
			)}

			<div id="success_contents" class="flex_col col_2">
				<article class="gui_main_text_color_on_bg">
					<LeveledHTMLText>{page_introduction}</LeveledHTMLText>

					<div class="success_actions">
						<div class="entries_btn">
							<ActionLink
								_href={`/gallery/${promotion.id}/${stage.id}`}
							>Back to the gallery</ActionLink>
						</div>
					</div>
				</article>

				<div class="epBox epModule main_contents_flex">
					{props.children}
				</div>
			</div>
		</div>
	)
}

const RankingSlot = ({ login_id, participant, promotion, stage, ...props }) => {
	const user = participant.Participation;
	const vote_info = participant.StageVote;

	const is_builder = props?.is_builder ?? false;
	const in_layer = props?.in_layer ?? false;

	const name_in_ranking = getVisualName(stage, user, props.display_name_as_nickname)

	const vote_count = parseInt(vote_info.points);
	const hide_username_and_date = stage?.gallery_hide_username ?? false;

	const voting_type = stage?.voting_type ?? 'simple';
	const vote_units_sing = (voting_type == 'simple') ? "{num} vote" : "{num} point";
	const vote_units_plur = (voting_type == 'simple') ? "{num} votes" : "{num} points";

	let participation = localizeFields([{
		...participant.Participation,
		short_description:participant.Participation.extra?.short_description,
		loc:participant.Participation.extra?.loc ?? {},
	}], props?.locale)[0];

	participation = fakeShortDescription(participation);

	return (
		<tr class={((login_id == user.login_id) ? 'logged_user_in_ranking' : null)}>
			{(!in_layer) && (
				<td class="ranking_pos">
					<div>
						<p class="hb_secondaryFont">{(!is_builder) ? vote_info.position : (i + 1)}.</p>
					</div>
				</td>
			)}

			<td>
				<div style={{ gap:'0.6em' }}>
					<ul class="gui_multimedia_gallery gui_main_text_color_on_bg">
						<SingleGallery
							is_in_ranking={true}
							media_index={0}
							promotion={promotion}
							selected_entry={props.selected_participant}
							stage={stage}
							user={{ ...participant, Participation:participation }}
							in_registered={is_builder}
							openDetailLayer={props.toggleSelected}
							closeEntryAsLayer={() => props.toggleSelected(false)}
						/>
					</ul>

					{(!hide_username_and_date) && (
						<div style={{width:'100%'}}>
							<p class="gui_participant_name hb_mainFontWeightBold">
								<span class="gui_cutTextOnOverflow">{name_in_ranking}</span>
							</p>

							{(isNotEmpty((participant?.Participation?.extra ?? {}), 'short_description')) && (
								<div class="short_description" dangerouslySetInnerHTML={{ __html:participant.Participation.extra.short_description.replace(/\n/g, ' ') }} />
							)}

							<GalleryTags
								user={participant}
								promotion={promotion}
								classes="gui_not_in_top_tiny"
							/>
						</div>
					)}
				</div>
			</td>

			<td width="25%" class="entry_score">
				<div class="gui_flex_row_reverse">
					<p class="hb_mainFontWeightBold">
						<FormattedPlural
							value={vote_count}
							one={<FormattedMessage id={vote_units_sing} values={{num:vote_count}} />}
							other={<FormattedMessage id={vote_units_plur} values={{num:vote_count}} />}
						/>
					</p>
				</div>
			</td>
		</tr>
	)
}

const MultiEntriesRanking = ({ promotion, stages, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ ranking, setRanking ] = useState(false);
	const [ filters, setFilters ] = useState([]);
	const [ used_media_tags, setUsedMediaTags ] = useState([]);
	const [ selected_participant, setSelectedParticipant ] = useState(false);

	const total_in_top = 0;

	const participants = props?.participants ?? [{}];
	const autoload_data = props?.autoload_data ?? true;
	const display_name_as_nickname = promotion?.need_nickname ?? false;
	const is_builder = props?.is_builder ?? false;
	const in_layer = props?.in_layer ?? false;

	let stage_id = props?.stage_id ?? false;
	if(stage_id === false) {
		const use_params  = useParams();
		stage_id = use_params.stage_id;
	}

	const stage = stages.find((obj) => (obj.Stage.id == stage_id)).Stage;

	const display_customizing_compare = false;
	
	useEffect(() => {
		if(autoload_data) {
			$.get(getRemotedbUrl(`/stages/get_gallery_ranking/${promotion.id}/${stage_id}`), (res) => {
				if((res?.used_media_tags ?? []).length > 0 && (promotion?.available_media_tags ?? []).length > 0) {
					const _used_media_tags = [];
					res.used_media_tags.map((tag) => {
						const _tag = promotion.available_media_tags.find((t) => (t.id == tag.id));
						if(_tag == undefined) return;
						if((tag?.total ?? 0) == 0) return;
	
						_used_media_tags.push({
							..._tag,
							title:`${_tag.title} (${tag.total})`,
						});
					});
					setUsedMediaTags(_used_media_tags);
				}
				setRanking(res.data);
			}, 'JSON');
		}

		if(!is_builder) {
			document.getElementById('ep-global-contents').classList.add('force_wg40');

			return () => {
				document.getElementById('ep-global-contents').classList.remove('force_wg40');
			}
		}
	}, []);

	if(!stage) return null;

	const is_user_logged = ((window?.login_info ?? false) !== false) ? true : false;
	const display_votes_not_logged = ((promotion?.galleries_hide_votes_to_visitors ?? false) === false) ? true : false;
	const is_user_enabled_in_ranking = (!is_user_logged && !display_votes_not_logged) ? false : true;
	if(!is_user_enabled_in_ranking) {
		window.location.href = `${getRemotedbUrl(`/gallery/${promotion.id}/${stage.id}`)}`;
		return null;
	}

	let top = false;
	let users = [];

	if(autoload_data) {
		[ top, users ] = useRankingWithSpan(ranking, {
			total_in_top:stage?.voting_ranking_entries ?? 10,
			span:2,
			max_results:2,
			matching:(u) => (u.Participation.login_id == props.login_id),
			getPos:(u) => (u.StageVote.position),
			filter_tags:filters
		});
	} else {
		top = participants;
	}

	const wrapper_props = {
		intl:props.intl,
		in_layer:in_layer,
		promotion:promotion,
		stage:stage,
	};

	if(top === false) {
		return (
			<Wrapper {...wrapper_props}>
				<article class="entries_list" style={{minHeight:'300px'}}>
					<Spinner autotranslate={false}>{i18n("Loading the ranking")}...</Spinner>
				</article>
			</Wrapper>
		)
	}

	return (
		<Wrapper {...wrapper_props}>
			<div class="gui_ranking gui_multi_ranking gui_main_text_color_on_bg">
				<div>
					<div class={(!in_layer) ? 'gui_not_in_top_big' : null}>
						<SelectMultiple
							items={used_media_tags}
							onChange={setFilters}
							_class={{ other:'in_ranking' }}
						/>

						{(participants.length > 0) && (
							<table cellSpacing="0" cellPadding="0" style={{width:'100%'}}>
								<thead>
									{(top && top.length < 1) ? (
										<tr>
											<th class="hb_secondaryFont" colSpan={2}>
												<FormattedMessage id="Top {number}" values={{number:total_in_top}} />
											</th>

											<th class="hb_secondaryFont gui_text_align_end" width="25%">{i18n("Score")}</th>
										</tr>
									) : (
										<tr>
											{(!in_layer) && (
												<th class="hb_secondaryFont" />
											)}

											<th class={'hb_secondaryFont' + ((in_layer) ? ' gui_text_align_start' : '')} style={{ '--text-default-align':'start' }}>{i18n("Participant")}</th>

											<th class="hb_secondaryFont gui_text_align_end" width="25%">{i18n("Score")}</th>
										</tr>
									)}
								</thead>

								<tbody>
									{top && top.map((participant) => {
										return (
											<RankingSlot
												key={`u_${participant.Participation.id}`}
												participant={participant}
												in_layer={in_layer}
												login_id={props.login_id}
												display_customizing_compare={display_customizing_compare}
												display_name_as_nickname={display_name_as_nickname}
												is_builder={is_builder}
												promotion={promotion}
												selected_participant={selected_participant}
												stage={stage}
												toggleSelected={setSelectedParticipant}
												locale={props.locale}
											/>
										)
									})}

									{!in_layer && users.length > 0 && users.map((participant, i) => {
										if(participant.separator != undefined && participant.separator) {
											return (
												<tr key={`s_${i}`}>
													<td colSpan={3}>...</td>
												</tr>
											)
										}

										return (
											<RankingSlot
												key={`r_${participant.Participation.id}`}
												participant={participant}
												login_id={props.login_id}
												display_customizing_compare={display_customizing_compare}
												display_name_as_nickname={display_name_as_nickname}
												is_builder={is_builder}
												promotion={promotion}
												selected_participant={selected_participant}
												stage={{id:stage_id}}
												toggleSelected={setSelectedParticipant}
												locale={props.locale}
											/>
										)
									})}
								</tbody>
							</table>
						)}

						{(participants.length == 0) && (
							<p>{i18n("There are no participants")}</p>
						)}
					</div>
				</div>
			</div>
		</Wrapper>
	)
}

export default injectIntl(MultiEntriesRanking);